import type { ReactNode } from "react";
import {
  displayEmail,
  RequireAuth,
  RequireGroup,
  redirectToSignIn,
  signUpUrl,
  useSession,
} from "@jkast90/kast-common/auth";
import { authScreens, NotAllowedScreen, type NotAllowedScreenProps } from "@jkast90/kast-common/ui";
import { signOut } from "../session";

const NOT_ADMIN: Omit<NotAllowedScreenProps, "email"> = {
  title: "This account isn't an admin",
  group: "admin",
  homeHref: "/",
  onSwitchAccount: () => void signOut(),
};

// Heads off to sign in at once, coming back to this page; the prompt stays on
// screen meanwhile, and if the redirect is slow.
const screens = () =>
  authScreens({
    signIn: {
      onSignIn: () => redirectToSignIn(),
      autoSignIn: true,
      signUpHref: signUpUrl(),
      detail: "Sign in with your kast-dev account to use the admin tools.",
    },
    notAllowed: NOT_ADMIN,
  });

/** Its children for a signed-in user; the sign-in prompt (and the trip to sign in) otherwise. */
export function SignedIn({ children }: { children: ReactNode }) {
  const { loading, prompt } = screens();
  return (
    <RequireAuth loading={loading} prompt={prompt}>
      {children}
    </RequireAuth>
  );
}

/** Its children for a member of the admin group; the not-admin page for anyone else signed in. */
export function AdminOnly({ children }: { children: ReactNode }) {
  return (
    <RequireGroup group="admin" {...screens()}>
      {children}
    </RequireGroup>
  );
}

/** Signed in, but not an admin: say so, rather than redirect silently. */
export function NotAdmin() {
  return <NotAllowedScreen {...NOT_ADMIN} email={displayEmail(useSession().user)} />;
}
